import { t } from "@redotech/redo-i18n";
import type { ProductEntry } from "./products";

// PRODUCTS keeps English copy because redo/onboarding/api mirrors it for
// server-side labels; these getters translate it at display time. Contexts
// keep setup's translations apart from same-English keys in other catalogs,
// which share one merged bundle.

export function getProductLabel(
  product: Pick<ProductEntry, "id" | "label">,
): string {
  switch (product.id) {
    case "returns":
      return t("Returns", { context: "Redo product name" });
    case "order-editing":
      return t("Order Editing", { context: "Redo product name" });
    case "shipping-fulfillment":
      return t("Shipping & Fulfillment", { context: "Redo product name" });
    case "chargebacks":
      return t("Chargebacks", { context: "Redo product name" });
    case "withdrawals":
      return t("Withdrawals", { context: "Redo product name" });
    case "contextual":
      return "Contextual";
    case "email-sms":
      return t("Email & SMS", { context: "Redo product name" });
    case "aeo":
      return t("Answer Engine Optimization", { context: "Redo product name" });
    case "checkout-optimization":
      return t("Checkout Optimization", { context: "Redo product name" });
    case "ai-sales-support":
      return t("AI Sales + Support", { context: "Redo product name" });
    case "inventory-management":
      return t("Inventory Management", { context: "Redo product name" });
    case "recover":
    case "agentic-catalog":
      return product.label;
  }
}

export function getProductSetupHeading(
  product: Pick<ProductEntry, "id" | "label">,
): string {
  switch (product.id) {
    case "returns":
      return t("Set up Returns");
    case "order-editing":
      return t("Set up Order Editing");
    case "shipping-fulfillment":
      return t("Set up Shipping & Fulfillment");
    case "chargebacks":
      return t("Set up Chargebacks");
    case "withdrawals":
      return t("Set up Withdrawals");
    case "contextual":
      return t("Set up {{productName}}", { productName: "Contextual" });
    case "email-sms":
      return t("Set up Email & SMS");
    case "aeo":
      return t("Set up Answer Engine Optimization");
    case "checkout-optimization":
      return t("Set up Checkout Optimization");
    case "ai-sales-support":
      return t("Set up AI Sales + Support");
    case "inventory-management":
      return t("Set up Inventory Management");
    case "recover":
    case "agentic-catalog":
      return t("Set up {{productName}}", { productName: product.label });
  }
}

export function getProductDescription(
  product: Pick<ProductEntry, "id" | "description">,
): string {
  switch (product.id) {
    case "returns":
      return t(
        "We'll walk you through creating an automated Returns Portal that personalizes every experience, turning refunds into exchanges and claims into opportunities to build trust. Then we'll set up {{productName}}, our signature returns label coverage product. Your customers can opt in to coverage for a low fee, and we'll cover the cost of return labels. Portal and {{productName}} are absolutely free for you",
        { productName: "Checkout+" },
      );
    case "order-editing":
      return t(
        "Let customers update eligible orders before they reach fulfillment.",
      );
    case "shipping-fulfillment":
      return t(
        "Set up the channels, carriers, packaging, and automations that power fulfillment.",
      );
    case "chargebacks":
      return t(
        "Connect dispute sources and automate the evidence used to recover chargebacks.",
      );
    case "withdrawals":
      return t(
        "Configure and manage withdrawal requests alongside your returns operations.",
      );
    case "contextual":
      return t(
        "Connect first-party storefront events to the marketing destinations you already use.",
      );
    case "email-sms":
      return t(
        "Plan branded email and SMS experiences with the Redo marketing team.",
      );
    case "aeo":
      return t(
        "Improve how answer engines discover, understand, and describe your brand.",
      );
    case "checkout-optimization":
      return t(
        "Design checkout experiences that increase conversion and average order value.",
      );
    case "ai-sales-support":
      return t(
        "Create AI-powered shopping and support experiences grounded in your brand.",
      );
    case "inventory-management":
      return t(
        "Plan inventory visibility and operations across your locations and sales channels.",
      );
    case "recover":
    case "agentic-catalog":
      return product.description;
  }
}

export function getProductSubtextLabel(subtext: string): string {
  switch (subtext) {
    case "5 minute set up":
      return t("5 minute set up");
    case "10 minute set up":
      return t("10 minute set up");
    case "15 minute set up":
      return t("15 minute set up");
    case "20 minute set up":
      return t("20 minute set up");
    case "Schedule a call":
      return t("Schedule a call", { context: "book a call with Redo" });
    default:
      return subtext;
  }
}

/** Step labels, nav groups, and setup highlight titles. */
export function getProductStepLabel(label: string): string {
  switch (label) {
    case "Activate":
      return t("Activate");
    case "Ad Platforms":
      return t("Ad Platforms");
    case "App Embed":
      return t("App Embed");
    case "Automations":
      return t("Automations");
    case "Billing":
      return t("Billing", { context: "setup step name" });
    case "Carriers":
      return t("Carriers", { context: "setup step name" });
    case "Checkout+":
      return "Checkout+";
    case "Communications":
      return t("Communications");
    case "Complete":
      return t("Complete", { context: "setup step name" });
    case "Coverage":
      return t("Coverage", { context: "setup step name" });
    case "Current Software":
      return t("Current Software");
    case "Customer Actions":
      return t("Customer Actions");
    case "Customer Experience":
      return t("Customer Experience");
    case "Destinations":
      return t("Destinations");
    case "Dispute Sources":
      return t("Dispute Sources");
    case "Email & SMS":
      return t("Email & SMS", { context: "setup step name" });
    case "Experience":
      return t("Experience", { context: "setup step name" });
    case "Feedback":
      return t("Feedback");
    case "Finalization":
      return t("Finalization");
    case "Go Live":
      return t("Go Live");
    case "How It Works":
      return t("How It Works");
    case "Integrations":
      return t("Integrations", { context: "setup step name" });
    case "International":
      return t("International", { context: "setup step name" });
    case "Launch":
      return t("Launch");
    case "Operations":
      return t("Operations");
    case "Order Automations":
      return t("Order Automations");
    case "Overview":
      return t("Overview");
    case "Packaging":
      return t("Packaging");
    case "Packing Slip":
      return t("Packing Slip");
    case "Permissions":
      return t("Permissions", { context: "app permissions setup step" });
    case "Platforms":
      return t("Platforms");
    case "Policy":
      return t("Policy");
    case "Return Policy":
      return t("Return Policy", { context: "setup step name" });
    case "Return Portal Preview":
      return t("Return Portal Preview");
    case "Return Processing":
      return t("Return Processing");
    case "Returns Policy":
      return t("Returns Policy");
    case "Sales Channels":
      return t("Sales Channels", { context: "setup step name" });
    case "Schedule a call":
      return t("Schedule a call", { context: "book a call with Redo" });
    case "Setup":
      return t("Setup");
    case "Subdomain Setup":
      return t("Subdomain Setup");
    case "Tag Setup":
      return t("Tag Setup");
    case "Timing":
      return t("Timing");
    case "Views":
      return t("Views", { context: "saved order views setup step" });
    case "Widget Preview":
      return t("Widget Preview");
    default:
      return label;
  }
}

export function getProductHighlightDescription(description: string): string {
  switch (description) {
    case "Import your existing policy to automatically configure return window, eligible items, and exchange/refund rules":
      return t(
        "Import your existing policy to automatically configure return window, eligible items, and exchange/refund rules",
      );
    case "Configure emails your customers receive during a return.":
      return t("Configure emails your customers receive during a return.");
    case "Customize our Returns Portal to match your brand's design language exactly":
      return t(
        "Customize our Returns Portal to match your brand's design language exactly",
      );
    case "Configure how your customers can opt into returns coverage":
      return t("Configure how your customers can opt into returns coverage");
    default:
      return description;
  }
}
